import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, FileText, User, Calendar, UserCheck, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi, authApi } from '../services/authApi';
import LoadingSpinner from '../components/LoadingSpinner';
import DentistPicker from '../components/DentistPicker';
import { useToast } from '../hooks/use-toast';

const AssignProcedurePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, practice } = useAuth();
  const { toast } = useToast();
  
  // Get pre-selected patient from URL parameters
  const preSelectedPatientId = searchParams.get('patientId');
  const preSelectedPatientName = searchParams.get('patientName');
  
  const [patients, setPatients] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Multi-procedure selection state
  const [isMultiProcedureMode, setIsMultiProcedureMode] = useState(false);
  const [selectedProcedures, setSelectedProcedures] = useState(new Set());
  
  const [formData, setFormData] = useState({
    patientId: preSelectedPatientId || '',
    procedureId: '',
    performedDate: new Date().toISOString().split('T')[0],
    dentistId: '',
    dentistName: user?.firstName ? `${user.firstName} ${user.lastName}` : '',
    followUpDate: ''
  });
  
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [procedureSearchTerm, setProcedureSearchTerm] = useState('');
  const [errors, setErrors] = useState({});

  // Filter patients based on search term
  const filteredPatients = React.useMemo(() => {
    if (!patientSearchTerm) return patients;
    const searchLower = patientSearchTerm.toLowerCase();
    return patients.filter(patient => {
      const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
      const email = (patient.email || '').toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }, [patients, patientSearchTerm]);

  // Filter procedures based on search term
  const filteredProcedures = React.useMemo(() => {
    if (!procedureSearchTerm) return procedures;
    const searchLower = procedureSearchTerm.toLowerCase();
    return procedures.filter(procedure => {
      const name = (procedure.name || '').toLowerCase();
      const specialty = (procedure.specialty || '').toLowerCase();
      return name.includes(searchLower) || specialty.includes(searchLower);
    });
  }, [procedures, procedureSearchTerm]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load essential data first
      const [patientsResponse, proceduresResponse] = await Promise.all([
        practiceApi.getPatients(),
        authApi.getProcedures() // Use global procedures API instead of practice-specific
      ]);
      
      // The API functions already return response.data, which contains {success: true, data: [...]}
      setPatients(patientsResponse.data || []);
      setProcedures(proceduresResponse.data || []);
      
      // Try to load dentists from new API, fallback to old doctors API
      try {
        const dentistsResponse = await practiceApi.getDentists();
        setDentists(dentistsResponse.data || []);
      } catch (dentistsError) {
        console.warn('Failed to load dentists, trying doctors API:', dentistsError);
        // Fallback to old doctors API and convert format
        try {
          const doctorsResponse = await practiceApi.getPracticeDoctors();
          const doctorsData = doctorsResponse.data || [];
          // Convert doctors format to dentists format
          const convertedDentists = doctorsData.map(doctor => ({
            id: doctor.id,
            firstName: doctor.firstName || doctor.name?.split(' ')[0] || '',
            lastName: doctor.lastName || doctor.name?.split(' ').slice(1).join(' ') || '',
            email: doctor.email || '',
            phone: doctor.phone || '',
            specialties: doctor.specialties || []
          }));
          setDentists(convertedDentists);
        } catch (doctorsError) {
          console.warn('Failed to load doctors:', doctorsError);
          // Set default dentist from current user
          if (user?.firstName && user?.lastName) {
            setDentists([{
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email || '',
              phone: user.phone || '',
              specialties: []
            }]);
          }
        }
      }
      
      // Debug logging
      console.log('Patients loaded:', patientsResponse.data?.length || 0);
      console.log('Procedures loaded:', proceduresResponse.data?.length || 0);
      
    } catch (error) {
      console.error('Load data error:', error);
      toast({
        title: "Error",
        description: "Failed to load patients and procedures",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Special handling for procedure selection
    if (field === 'procedureId' && value === 'request-new') {
      // Navigate to request procedure page
      navigate('/request-procedure');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Multi-procedure selection functions
  const handleProcedureToggle = (procedureId, procedureName) => {
    const newSelected = new Set(selectedProcedures);
    if (newSelected.has(procedureId)) {
      newSelected.delete(procedureId);
    } else {
      newSelected.add(procedureId);
    }
    setSelectedProcedures(newSelected);
    
    // Clear procedure errors when user selects procedures
    if (errors.procedureId || errors.procedures) {
      setErrors(prev => ({
        ...prev,
        procedureId: null,
        procedures: null
      }));
    }
  };

  const handleSelectAllProcedures = () => {
    const allProcedureIds = new Set(procedures.map(p => p.id));
    setSelectedProcedures(allProcedureIds);
  };

  const handleDeselectAllProcedures = () => {
    setSelectedProcedures(new Set());
  };

  const toggleProcedureMode = () => {
    setIsMultiProcedureMode(!isMultiProcedureMode);
    // Clear selections when switching modes
    setSelectedProcedures(new Set());
    setFormData(prev => ({
      ...prev,
      procedureId: ''
    }));
    // Clear any procedure-related errors
    setErrors(prev => ({
      ...prev,
      procedureId: null,
      procedures: null
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.patientId) {
      newErrors.patientId = 'Please select a patient';
    }
    
    // Validate procedures based on mode
    if (isMultiProcedureMode) {
      if (selectedProcedures.size === 0) {
        newErrors.procedures = 'Please select at least one procedure';
      }
    } else {
      if (!formData.procedureId) {
        newErrors.procedureId = 'Please select a procedure';
      }
    }
    
    // Performed date is now optional
    
    if (!formData.dentistName.trim()) {
      newErrors.dentistName = 'Dentist name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const selectedPatient = patients.find(p => p.id === formData.patientId);
      
      if (isMultiProcedureMode) {
        // Multi-procedure assignment
        const selectedProcedureList = Array.from(selectedProcedures).map(procId => {
          const procedure = procedures.find(p => p.id === procId);
          return {
            procedureId: procId,
            procedureName: procedure.name
          };
        });
        
        const multiAssignmentData = {
          patientId: formData.patientId,
          procedures: selectedProcedureList,
          performedDate: new Date(formData.performedDate).toISOString(),
          dentistName: formData.dentistName.trim(),
          followUpDate: formData.followUpDate ? new Date(formData.followUpDate).toISOString() : null
        };
        
        await practiceApi.assignMultipleProcedures(multiAssignmentData);
        
        toast({
          title: "Success!",
          description: `${selectedProcedureList.length} procedures have been assigned to ${selectedPatient.firstName} ${selectedPatient.lastName}.`,
          variant: "default",
        });
        
      } else {
        // Single procedure assignment (existing functionality)
        const selectedProcedure = procedures.find(p => p.id === formData.procedureId);
        
        const assignmentData = {
          patientId: formData.patientId,
          procedureId: formData.procedureId,
          procedureName: selectedProcedure.name,
          performedDate: new Date(formData.performedDate).toISOString(),
          dentistName: formData.dentistName.trim(),
          followUpDate: formData.followUpDate ? new Date(formData.followUpDate).toISOString() : null
        };
        
        await practiceApi.assignProcedure(assignmentData);
        
        toast({
          title: "Success!",
          description: `${selectedProcedure.name} has been assigned to ${selectedPatient.firstName} ${selectedPatient.lastName}.`,
          variant: "default",
        });
      }
      
      // Navigate back to dashboard after short delay
      setTimeout(() => {
        navigate('/');
      }, 1500);
      
    } catch (error) {
      console.error('Assign procedure error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to assign procedure. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="flex items-center"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Assign Procedures</h1>
              <p className="text-gray-600">
                Assign post-operative care instructions to a patient
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2 text-blue-600" />
              Procedure Assignment
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preSelectedPatientName && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <UserCheck className="h-5 w-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      Patient Pre-selected
                    </p>
                    <p className="text-sm text-green-700">
                      Ready to assign a procedure to <span className="font-semibold">{preSelectedPatientName}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Procedure Mode Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="multiProcedureMode"
                      checked={isMultiProcedureMode}
                      onChange={toggleProcedureMode}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <Label htmlFor="multiProcedureMode" className="text-sm font-medium text-gray-700">
                      Select multiple procedures
                    </Label>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {isMultiProcedureMode 
                    ? `${selectedProcedures.size} procedure${selectedProcedures.size !== 1 ? 's' : ''} selected`
                    : 'Single procedure mode'
                  }
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="patientId">Patient *</Label>
                  <div className="space-y-2">
                    {/* Always show search box if no patient selected */}
                    {!formData.patientId && (
                      <input
                        type="text"
                        placeholder="Type patient name to search..."
                        value={patientSearchTerm}
                        onChange={(e) => setPatientSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                    
                    {/* Selected Patient Display - ALWAYS shows when patient is selected */}
                    {formData.patientId && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
                        <div>
                          <p className="font-medium text-blue-900">
                            {patients.find(p => p.id === formData.patientId)?.firstName || preSelectedPatientName?.split(' ')[0] || 'Loading...'}{' '}
                            {patients.find(p => p.id === formData.patientId)?.lastName || preSelectedPatientName?.split(' ').slice(1).join(' ') || ''}
                          </p>
                          <p className="text-sm text-blue-700">
                            {patients.find(p => p.id === formData.patientId)?.email || 'Patient selected'}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setFormData(prev => ({ ...prev, patientId: '' }));
                            setPatientSearchTerm('');
                          }}
                          className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                        >
                          Change Patient
                        </button>
                      </div>
                    )}
                    
                    {/* Clickable Patient List */}
                    {patientSearchTerm && !formData.patientId && (
                      <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                        {filteredPatients.length > 0 ? (
                          filteredPatients.map((patient) => {
                            // Check for duplicate names
                            const duplicates = filteredPatients.filter(p => 
                              p.firstName === patient.firstName && p.lastName === patient.lastName
                            );
                            const showEmail = duplicates.length > 1;
                            
                            return (
                              <div
                                key={patient.id}
                                onClick={() => {
                                  handleInputChange('patientId', patient.id);
                                  setPatientSearchTerm('');
                                }}
                                className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                              >
                                <p className="font-medium text-gray-900">
                                  {patient.firstName} {patient.lastName}
                                </p>
                                {showEmail && patient.email && (
                                  <p className="text-sm text-gray-600">{patient.email}</p>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="p-3 text-center text-gray-500">
                            No patients match "{patientSearchTerm}"
                          </div>
                        )}
                      </div>
                    )}
                    
                    {patientSearchTerm && filteredPatients.length > 0 && !formData.patientId && (
                      <p className="text-xs text-gray-600">
                        {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''} found - click to select
                      </p>
                    )}
                  </div>
                  {errors.patientId && (
                    <p className="text-sm text-red-500 mt-1">{errors.patientId}</p>
                  )}
                  {patients.length === 0 && (
                    <p className="text-sm text-blue-600 mt-1">
                      <Button
                        type="button"
                        variant="link"
                        className="p-0 h-auto"
                        onClick={() => navigate('/add-patient')}
                      >
                        Add a patient first
                      </Button>
                    </p>
                  )}
                </div>
                
                {/* Dynamic Procedure Selection */}
                <div>
                  <Label htmlFor="procedureId">
                    {isMultiProcedureMode ? 'Procedures *' : 'Procedure *'}
                  </Label>
                  
                  {isMultiProcedureMode ? (
                    // Multi-procedure selection with checkboxes
                    <div className="space-y-2">
                      {/* Procedure Search for Multi-select */}
                      <input
                        type="text"
                        placeholder="Type to filter procedures..."
                        value={procedureSearchTerm}
                        onChange={(e) => setProcedureSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      
                      {/* Multi-select controls */}
                      <div className="flex items-center justify-between text-xs">
                        <button
                          type="button"
                          onClick={handleSelectAllProcedures}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleDeselectAllProcedures}
                          className="text-red-600 hover:text-red-800"
                        >
                          Deselect All
                        </button>
                      </div>
                      
                      {/* Checkbox list */}
                      <div className={`border rounded-md max-h-60 overflow-y-auto ${errors.procedures ? 'border-red-500' : 'border-gray-300'}`}>
                        {filteredProcedures.length > 0 ? (
                          filteredProcedures.map((procedure) => (
                            <div
                              key={procedure.id}
                              className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                            >
                              <input
                                type="checkbox"
                                id={`procedure-${procedure.id}`}
                                checked={selectedProcedures.has(procedure.id)}
                                onChange={() => handleProcedureToggle(procedure.id, procedure.name)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor={`procedure-${procedure.id}`}
                                className="ml-3 flex flex-col cursor-pointer flex-1"
                              >
                                <span className="font-medium text-sm">{procedure.name}</span>
                                <span className="text-xs text-gray-500">{procedure.specialty}</span>
                              </label>
                            </div>
                          ))
                        ) : (
                          <div className="p-3 text-center text-gray-500">
                            {procedureSearchTerm ? `No procedures match "${procedureSearchTerm}"` : 'No procedures available'}
                          </div>
                        )}
                      </div>
                      
                      {/* Show filtered count */}
                      {procedureSearchTerm && (
                        <p className="text-xs text-gray-600">
                          Showing {filteredProcedures.length} of {procedures.length} procedures
                        </p>
                      )}
                    </div>
                  ) : (
                    // Single procedure selection with search
                    <div className="space-y-2">
                      {/* Procedure Search Input */}
                      <input
                        type="text"
                        placeholder="Type to search procedures..."
                        value={procedureSearchTerm}
                        onChange={(e) => setProcedureSearchTerm(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.procedureId ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      
                      {/* Selected Procedure Display */}
                      {formData.procedureId && (
                        <div className="p-3 bg-green-50 border border-green-200 rounded-md flex items-center justify-between">
                          <div>
                            <p className="font-medium text-green-900">
                              {procedures.find(p => p.id === formData.procedureId)?.name || 'Selected Procedure'}
                            </p>
                            <p className="text-sm text-green-700">
                              {procedures.find(p => p.id === formData.procedureId)?.specialty || ''}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setFormData(prev => ({ ...prev, procedureId: '' }));
                              setProcedureSearchTerm('');
                            }}
                            className="px-3 py-1 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
                          >
                            Change
                          </button>
                        </div>
                      )}
                      
                      {/* Procedure List - shows when searching and no procedure selected */}
                      {procedureSearchTerm && !formData.procedureId && (
                        <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                          {filteredProcedures.length > 0 ? (
                            <>
                              {filteredProcedures.map((procedure) => (
                                <div
                                  key={procedure.id}
                                  onClick={() => {
                                    handleInputChange('procedureId', procedure.id);
                                    setProcedureSearchTerm('');
                                  }}
                                  className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                                >
                                  <p className="font-medium text-gray-900">{procedure.name}</p>
                                  <p className="text-sm text-gray-600">{procedure.specialty}</p>
                                </div>
                              ))}
                              {/* Request New Procedure Option */}
                              <div
                                onClick={() => navigate('/request-procedure')}
                                className="p-3 hover:bg-blue-50 cursor-pointer border-t border-gray-200 bg-gray-50"
                              >
                                <div className="flex items-center text-blue-600">
                                  <Plus className="h-4 w-4 mr-2" />
                                  <span className="font-medium">Request New Procedure</span>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="p-3 text-center">
                              <p className="text-gray-500">No procedures match "{procedureSearchTerm}"</p>
                              <button
                                type="button"
                                onClick={() => navigate('/request-procedure')}
                                className="mt-2 text-blue-600 hover:text-blue-800 text-sm flex items-center justify-center mx-auto"
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Request New Procedure
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Show all procedures if no search and no selection */}
                      {!procedureSearchTerm && !formData.procedureId && (
                        <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                          {procedures.length > 0 ? (
                            <>
                              {procedures.slice(0, 10).map((procedure) => (
                                <div
                                  key={procedure.id}
                                  onClick={() => {
                                    handleInputChange('procedureId', procedure.id);
                                  }}
                                  className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                                >
                                  <p className="font-medium text-gray-900">{procedure.name}</p>
                                  <p className="text-sm text-gray-600">{procedure.specialty}</p>
                                </div>
                              ))}
                              {procedures.length > 10 && (
                                <div className="p-3 text-center text-gray-500 text-sm bg-gray-50">
                                  Type to search {procedures.length - 10} more procedures...
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="p-3 text-center text-gray-500">
                              No procedures available
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Procedure count hint */}
                      {procedureSearchTerm && filteredProcedures.length > 0 && !formData.procedureId && (
                        <p className="text-xs text-gray-600">
                          {filteredProcedures.length} procedure{filteredProcedures.length !== 1 ? 's' : ''} found - click to select
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Error messages */}
                  {errors.procedureId && (
                    <p className="text-sm text-red-500 mt-1">{errors.procedureId}</p>
                  )}
                  {errors.procedures && (
                    <p className="text-sm text-red-500 mt-1">{errors.procedures}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="performedDate">Performed Date (Optional)</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="performedDate"
                      type="date"
                      value={formData.performedDate}
                      onChange={(e) => handleInputChange('performedDate', e.target.value)}
                      disabled={submitting}
                      className={`pl-10 ${errors.performedDate ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.performedDate && (
                    <p className="text-sm text-red-500 mt-1">{errors.performedDate}</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="followUpDate">Follow-up Date & Time (Optional)</Label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="followUpDate"
                      type="datetime-local"
                      value={formData.followUpDate}
                      onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                      disabled={submitting}
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Set when the follow-up email should be sent (overrides practice settings)
                  </p>
                </div>
              </div>

                <div>
                  <Label htmlFor="dentistName">Dentist *</Label>
                  <DentistPicker
                    dentists={dentists}
                    selectedId={formData.dentistId}
                    selectedName={formData.dentistName}
                    onChange={({ id, dentist, name }) => {
                      setFormData((prev) => ({
                        ...prev,
                        dentistId: id,
                        dentistName: dentist
                          ? `${dentist.firstName} ${dentist.lastName}`
                          : name,
                      }));
                    }}
                    onDentistAdded={(d) =>
                      setDentists((prev) => {
                        const without = prev.filter((x) => x.id !== d.id);
                        return [...without, d].sort((a, b) =>
                          (a.lastName || '').localeCompare(b.lastName || '')
                        );
                      })
                    }
                    error={errors.dentistName}
                    testIdPrefix="procedure"
                  />
                  {errors.dentistName && (
                    <p className="text-sm text-red-500 mt-1">{errors.dentistName}</p>
                  )}
                </div>

              {/* Custom fields removed - these will be editable only during print/edit */}

              <div className="flex justify-end space-x-3 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting || patients.length === 0}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {submitting ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Assigning...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      {isMultiProcedureMode 
                        ? `Assign ${selectedProcedures.size} Procedure${selectedProcedures.size !== 1 ? 's' : ''}`
                        : 'Assign Procedure'
                      }
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssignProcedurePage;